/**
 * Standardized error handling for API operations
 */

import type { PostgrestError } from '@supabase/supabase-js';
import type { ApiError, ApiResponse } from './types/common';

/**
 * Error codes for different types of API errors
 */
export enum ApiErrorCode {
  NOT_FOUND = 'NOT_FOUND',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CONFLICT = 'CONFLICT',
  DATABASE_ERROR = 'DATABASE_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Convert a Postgres error to a standardized API error
 */
export function postgrestErrorToApiError(error: PostgrestError): ApiError {
  const apiError: ApiError = {
    message: error.message,
    code: error.code,
    details: error.details,
    hint: error.hint,
    originalError: error,
  };

  // Map common Postgres error codes to semantic error codes
  if (error.code === 'PGRST116') {
    apiError.code = ApiErrorCode.NOT_FOUND;
    apiError.statusCode = 404;
  } else if (error.code === '23505') {
    apiError.code = ApiErrorCode.CONFLICT;
    apiError.statusCode = 409;
  } else if (error.code === '23503') {
    apiError.code = ApiErrorCode.VALIDATION_ERROR;
    apiError.statusCode = 400;
  } else {
    apiError.code = ApiErrorCode.DATABASE_ERROR;
    apiError.statusCode = 500;
  }

  return apiError;
}

/**
 * Convert a generic error to a standardized API error
 */
export function errorToApiError(error: unknown): ApiError {
  if (error instanceof Error) {
    return {
      message: error.message,
      code: ApiErrorCode.UNKNOWN_ERROR,
      statusCode: 500,
      originalError: error,
    };
  }

  return {
    message: 'An unknown error occurred',
    code: ApiErrorCode.UNKNOWN_ERROR,
    statusCode: 500,
  };
}

/**
 * Create a standardized error response
 */
export function createErrorResponse<T = null>(error: unknown): ApiResponse<T> {
  const apiError = error && typeof error === 'object' && 'code' in error && 'message' in error
    ? postgrestErrorToApiError(error as PostgrestError)
    : errorToApiError(error);

  return {
    data: null,
    error: apiError,
    success: false,
  };
}

/**
 * Create a standardized success response
 */
export function createSuccessResponse<T>(data: T): ApiResponse<T> {
  return {
    data,
    error: null,
    success: true,
  };
}

/**
 * Custom error class for API errors
 */
export class ApiException extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly details?: string;
  public readonly hint?: string;

  constructor(message: string, code: string = ApiErrorCode.UNKNOWN_ERROR, statusCode: number = 500) {
    super(message);
    this.name = 'ApiException';
    this.code = code;
    this.statusCode = statusCode;
  }

  toApiError(): ApiError {
    return {
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      details: this.details,
      hint: this.hint,
      originalError: this,
    };
  }
}
